import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface URLStateOptions {
  persistInLocalStorage?: boolean;
  localStorageKey?: string;
}

/**
 * Custom hook to manage state synchronized with URL query parameters
 * @param key - The query parameter key
 * @param defaultValue - The default value if not in URL
 * @param options - Additional options for persistence
 */
export function useURLState<T extends string>(
  key: string,
  defaultValue: T,
  options: URLStateOptions = {}
): [T, (value: T) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const { persistInLocalStorage = false, localStorageKey } = options;

  // Get initial value from URL, localStorage, or default
  const getInitialValue = (): T => {
    const urlParams = new URLSearchParams(location.search);
    const urlValue = urlParams.get(key);
    
    if (urlValue) {
      return urlValue as T;
    }

    if (persistInLocalStorage && localStorageKey) {
      const storedValue = localStorage.getItem(localStorageKey);
      if (storedValue) {
        return storedValue as T;
      }
    }

    return defaultValue;
  };

  const [value, setValue] = useState<T>(getInitialValue);

  // Update URL when value changes
  const updateValue = useCallback((newValue: T) => {
    setValue(newValue);

    // Update URL
    const urlParams = new URLSearchParams(location.search);
    if (newValue && newValue !== defaultValue) {
      urlParams.set(key, newValue);
    } else {
      urlParams.delete(key);
    }

    // Navigate to new URL without page reload
    const newSearch = urlParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      },
      { replace: true }
    );

    // Update localStorage if enabled
    if (persistInLocalStorage && localStorageKey) {
      if (newValue && newValue !== defaultValue) {
        localStorage.setItem(localStorageKey, newValue);
      } else {
        localStorage.removeItem(localStorageKey);
      }
    }
  }, [key, defaultValue, location, navigate, persistInLocalStorage, localStorageKey]);

  // Sync with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlValue = urlParams.get(key);
    
    if (urlValue && urlValue !== value) {
      setValue(urlValue as T);
    } else if (!urlValue && value !== defaultValue) {
      setValue(defaultValue);
    }
  }, [location.search, key, defaultValue]);

  return [value, updateValue];
}

/**
 * Hook to manage multiple URL state parameters at once
 */
export function useURLStates<T extends Record<string, string | undefined>>(
  defaultValues: T,
  options: URLStateOptions = {}
): {
  values: T;
  setValue: (key: keyof T, value: string) => void;
  setValues: (updates: Partial<T>) => void;
  resetAll: () => void;
} {
  const location = useLocation();
  const navigate = useNavigate();
  const { persistInLocalStorage = false, localStorageKey } = options;

  // Get initial values
  const getInitialValues = (): T => {
    const urlParams = new URLSearchParams(location.search);
    const values: T = { ...defaultValues };

    // Load from URL
    for (const key in defaultValues) {
      const urlValue = urlParams.get(key);
      if (urlValue) {
        values[key] = urlValue as T[Extract<keyof T, string>];
      }
    }

    // Load from localStorage if enabled
    if (persistInLocalStorage && localStorageKey) {
      const storedData = localStorage.getItem(localStorageKey);
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          Object.assign(values, parsedData);
        } catch (e) {
          console.error('Failed to parse localStorage data:', e);
        }
      }
    }

    return values;
  };

  const [values, setValuesState] = useState<T>(getInitialValues);

  // Update single value
  const setValue = useCallback((key: keyof T, value: string) => {
    setValuesState(prev => ({ ...prev, [key]: value }));
    updateURL({ ...values, [key]: value });
  }, [values]);

  // Update multiple values
  const setValues = useCallback((updates: Partial<T>) => {
    const newValues = { ...values, ...updates };
    setValuesState(newValues);
    updateURL(newValues);
  }, [values]);

  // Reset all values to defaults
  const resetAll = useCallback(() => {
    setValuesState(defaultValues);
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
    
    if (persistInLocalStorage && localStorageKey) {
      localStorage.removeItem(localStorageKey);
    }
  }, [defaultValues, location.pathname, navigate, persistInLocalStorage, localStorageKey]);

  // Update URL with new values
  const updateURL = (newValues: T) => {
    const urlParams = new URLSearchParams();

    for (const key in newValues) {
      const value = newValues[key];
      if (value && value !== defaultValues[key]) {
        urlParams.set(key, value);
      }
    }

    const newSearch = urlParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      },
      { replace: true }
    );

    // Update localStorage if enabled
    if (persistInLocalStorage && localStorageKey) {
      const dataToStore: Partial<T> = {};
      for (const key in newValues) {
        if (newValues[key] !== defaultValues[key]) {
          dataToStore[key] = newValues[key];
        }
      }
      
      if (Object.keys(dataToStore).length > 0) {
        localStorage.setItem(localStorageKey, JSON.stringify(dataToStore));
      } else {
        localStorage.removeItem(localStorageKey);
      }
    }
  };

  // Sync with URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const newValues: T = { ...defaultValues };
    
    for (const key in defaultValues) {
      const urlValue = urlParams.get(key);
      if (urlValue) {
        newValues[key] = urlValue as T[Extract<keyof T, string>];
      }
    }
    
    setValuesState(newValues);
  }, [location.search]);

  return {
    values,
    setValue,
    setValues,
    resetAll
  };
}